import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavLink, useParams } from 'react-router-dom';

import { ReactComponent as PencilIcon } from '@/assets/icons/pencil.svg';
import ActionsDropdown, {
  ActionsDropdownButton,
} from '@/components/ActionsDropdown';
import { ActionsWrap, Td, Thumbnail } from '@/components/Table';
import Text from '@/components/Text';
import { Tabs } from '@/pages/DistributionVideoSummary/components/DistributionVideoTable';
import { SyledPin } from '@/pages/TopTopics/components/TopTopicsTableRow';
import { DistributionProjectResource } from '@/types/resources';
import { numFormatter } from '@/utils/helpers';
import { RootState } from '@/utils/store';

import EditForm from './EditForm';

interface Props {
  project: DistributionProjectResource;
}

const DistributionProjectTableRow: FC<Props> = ({ project }) => {
  const [showEditForm, setShowEditForm] = useState(false);

  const { teamId } = useParams();

  const tableViewOptions = useSelector(
    (rootState: RootState) =>
      rootState.options.tableViews['distributionProjectSummaryTable'],
  );

  return (
    <tr>
      <Td>{project.id}</Td>
      {tableViewOptions['Fresh ID'] && <Td>{project.freshId}</Td>}
      <Td>
        <Thumbnail src={project.thumbnail} />
      </Td>
      <Td>
        <Text $type="smallHeadline">{project.title}</Text>
      </Td>
      {tableViewOptions['Production Team'] && (
        <Td>{project.productionTeam ?? '-'}</Td>
      )}
      {tableViewOptions['Last Release Date'] && (
        <Td>
          {project.lastReleaseDate ? (
            <>
              {project.lastReleaseDate.split(' ')[0]}
              <br />
              {project.lastReleaseDate.split(' ')[1]}
            </>
          ) : (
            '-'
          )}
        </Td>
      )}
      {tableViewOptions['Releases'] && (
        <Td>
          <NavLink
            to={`/teams/${teamId}/distribution/projects/${encodeURIComponent(
              project.id,
            )}`}
            state={{
              tab: Tabs.All,
            }}
          >
            <SyledPin />
            {project.releasesCount}
          </NavLink>
        </Td>
      )}
      {tableViewOptions['Last Release Views'] && (
        <Td>
          {project.lastReleaseViews
            ? numFormatter(project.lastReleaseViews)
            : '-'}
        </Td>
      )}
      {tableViewOptions['Best Release Views'] && (
        <Td>
          {project.bestReleaseViews
            ? numFormatter(project.bestReleaseViews)
            : '-'}
        </Td>
      )}
      {/* TODO: check if topics needed */}
      {/* {tableViewOptions["Title Topics"] && <Td>-</Td>} */}
      {/* {tableViewOptions["Video Types"] && <Td>-</Td>} */}
      <Td $actionsCol>
        <ActionsWrap>
          <ActionsDropdown>
            <ActionsDropdownButton
              as="button"
              onClick={() => setShowEditForm(true)}
            >
              <span>
                <PencilIcon />
              </span>
              Edit
            </ActionsDropdownButton>
          </ActionsDropdown>
        </ActionsWrap>
        <AnimatePresence>
          {showEditForm && (
            <EditForm
              onClose={() => setShowEditForm(false)}
              project={project}
            />
          )}
        </AnimatePresence>
      </Td>
    </tr>
  );
};

export default DistributionProjectTableRow;
